import { FC, useEffect, useMemo } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	TextField,
	Autocomplete,
	CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { getCountryListRequest } from 'redux/reducers/country/reducer';
import {
	searchDeliveryOperatorsRequest,
	createTariffRequest,
	updateTariffRequest,
	resetCreateTariffSuccess,
	resetUpdateTariffSuccess,
} from 'redux/reducers/euDdp/reducer';
import {
	selectDeliveryOperatorsSearchData,
	selectCreateTariffLoading,
	selectCreateTariffSuccess,
	selectUpdateTariffLoading,
	selectUpdateTariffSuccess,
} from 'redux/reducers/euDdp/selectors';
import { TTariffDialogProps } from './types';
import { CountrySelectMenu } from '../../../../ui/CountrySelectMenu';
import { getEmEnumsRequest } from '../../../../redux/reducers/emManagement/reducer';
import { EEmEnums } from '../../../../redux/reducers/emManagement/constants';
import { getEnumsSupportedCurrency } from '../../../../redux/reducers/emManagement/selectors';
import { ECountryType } from '../../../../ui/EasTable/types';

const validationSchema = Yup.object({
	price: Yup.number().required('Price is required').min(0, 'Price must be >= 0'),
	weightFrom: Yup.number()
		.required('Weight From is required')
		.integer('Must be an integer')
		.min(0, 'Must be >= 0'),
	weightTo: Yup.number()
		.required('Weight To is required')
		.integer('Must be an integer')
		.min(0, 'Must be >= 0')
		.test('greater-than-from', 'Weight To must be > Weight From', function (value) {
			return value == null || this.parent.weightFrom == null || value > this.parent.weightFrom;
		}),
	deliveryOperatorId: Yup.number().required('Delivery Operator is required'),
	destinationCountry: Yup.string().required('Destination Country is required'),
});

export const TariffDialog: FC<TTariffDialogProps> = ({ open, onClose, tariffToEdit }) => {
	const dispatch = useDispatch();
	const isEdit = !!tariffToEdit;

	const deliveryOperators = useSelector(selectDeliveryOperatorsSearchData);
	const createLoading = useSelector(selectCreateTariffLoading);
	const createSuccess = useSelector(selectCreateTariffSuccess);
	const updateLoading = useSelector(selectUpdateTariffLoading);
	const updateSuccess = useSelector(selectUpdateTariffSuccess);
	const enumsSupportedCurrency = useSelector(getEnumsSupportedCurrency);
	const loading = createLoading || updateLoading;

	useEffect(() => {
		if (!open) return;
		dispatch(getCountryListRequest());
		dispatch(searchDeliveryOperatorsRequest({ pageNumber: 1, pageSize: 10000 }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SUPPORTED_CURRENCY }));
	}, [dispatch, open]);

	const formik = useFormik({
		initialValues: {
			price: tariffToEdit?.price ?? ('' as unknown as number),
			currency: tariffToEdit?.currency ?? '',
			weightFrom: tariffToEdit?.weightFrom ?? ('' as unknown as number),
			weightTo: tariffToEdit?.weightTo ?? ('' as unknown as number),
			deliveryOperatorId: tariffToEdit?.deliveryOperatorId ?? ('' as unknown as number),
			destinationCountry: tariffToEdit?.destinationCountry ?? '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				price: Number(values.price),
				currency: values.currency,
				weightFrom: Number(values.weightFrom),
				weightTo: Number(values.weightTo),
				deliveryOperatorId: Number(values.deliveryOperatorId),
				destinationCountry: values.destinationCountry,
			};
			if (isEdit && tariffToEdit) {
				dispatch(updateTariffRequest({ id: tariffToEdit.id, body }));
			} else {
				dispatch(createTariffRequest(body));
			}
		},
	});

	useEffect(() => {
		if (createSuccess || updateSuccess) {
			dispatch(resetCreateTariffSuccess());
			dispatch(resetUpdateTariffSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createSuccess, updateSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	const operatorOptions = useMemo(
		() => deliveryOperators.map((op) => ({ label: `${op.name} (${op.code})`, value: op.id })),
		[deliveryOperators],
	);

	const selectedOperator = useMemo(
		() => operatorOptions.find((o) => o.value === formik.values.deliveryOperatorId) ?? null,
		[operatorOptions, formik.values.deliveryOperatorId],
	);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Tariff' : 'Add New Tariff'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<Autocomplete
						options={operatorOptions}
						getOptionLabel={(option) => option.label}
						value={selectedOperator}
						onChange={(_e, newValue) => {
							void formik.setFieldValue('deliveryOperatorId', newValue?.value ?? '');
						}}
						isOptionEqualToValue={(option, val) => option.value === val.value}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Delivery Operator"
								error={formik.touched.deliveryOperatorId && !!formik.errors.deliveryOperatorId}
								helperText={formik.touched.deliveryOperatorId && formik.errors.deliveryOperatorId}
								size="small"
							/>
						)}
					/>
					<CountrySelectMenu
						onChange={(newValue: string) => {
							void formik.setFieldValue('destinationCountry', newValue);
						}}
						countryCode={formik.values.destinationCountry}
						typeOfCountry={ECountryType.EU}
					/>
					<TextField
						label="Weight From (g)"
						name="weightFrom"
						type="number"
						size="small"
						value={formik.values.weightFrom}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.weightFrom && !!formik.errors.weightFrom}
						helperText={formik.touched.weightFrom && formik.errors.weightFrom}
					/>
					<TextField
						label="Weight To (g)"
						name="weightTo"
						type="number"
						size="small"
						value={formik.values.weightTo}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.weightTo && !!formik.errors.weightTo}
						helperText={formik.touched.weightTo && formik.errors.weightTo}
					/>
					<TextField
						label="Price"
						name="price"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.price}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.price && !!formik.errors.price}
						helperText={formik.touched.price && formik.errors.price}
					/>
					<Autocomplete
						options={
							enumsSupportedCurrency?.map((c) => ({
								label: c.displayName,
								value: c.displayName,
							})) ?? []
						}
						getOptionLabel={(option) => option.label}
						value={
							enumsSupportedCurrency
								?.map((c) => ({ label: c.displayName, value: c.displayName }))
								.find((o) => o.value === formik.values.currency) ?? null
						}
						onChange={(_e, newValue) => {
							void formik.setFieldValue('currency', newValue?.value ?? '');
						}}
						isOptionEqualToValue={(option, val) => option.value === val.value}
						size="small"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Currency Code"
								error={formik.touched.currency && !!formik.errors.currency}
								helperText={formik.touched.currency && (formik.errors.currency as string)}
							/>
						)}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
